import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useStateValue } from '../../StateProvider';
import {
    collectUserTalentAncestorIds,
    getCourseTalentIds,
    userCanParticipateInContest,
} from '../../utils/contestEligibility';
import { getDefaultDescription, getDefaultTutorialVideo, getDefaultValue } from '../../utils';
import {
    loadCourses,
    loadTalentDocument,
    loadUserTalentDocuments,
    loadUserTalents,
    resolveMediaUrl,
} from '../../utils/Network';
import ContestParticipation from '../contest/ContestParticipation';
import StepIndicator from '../contest/StepIndicator';
import UploadDocumentForm from '../forms/talent/UploadDocumentForm';
import SelectTalent from '../select/SelectTalent';
import LoadingOverlay from '../shared/LoadingOverlay';
import UserDocumentPreview from '../talent/UserDocumentPreview';
import { pickNewestNewContest } from '../widgets/WidgetNewContest';

/** Same tutorial as `UserTalents` / `ShowVideoModal` (submission how-to). */
const REQUIREMENTS_TUTORIAL_VIDEO_SRC = '/assets/video/register.mp4';

function talentMatchesContest(userTalent, contestIds) {
    if (!userTalent?.talent || !contestIds?.size) return false;
    const t = userTalent.talent;
    if (contestIds.has(t.id)) return true;
    const ancestors = Array.isArray(t.ancestor_ids) ? t.ancestor_ids : [];
    return ancestors.some((id) => contestIds.has(id));
}

function AddUserTalentModal({ onVideosChanged, onContestParticipationChange }) {

    const btnCloseModal = useRef(null);
    const requirementsVideoRef = useRef(null);
    const resolveGen = useRef(0);

    const [talent, setTalent] = useState(null);
    const [step, setStep] = useState(0);
    const [error, setError] = useState(null);
    const [upload_enabled, setUploadEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userDocument, setUserDocument] = useState(false);
    const [talentDocumentSpec, setTalentDocumentSpec] = useState(null);
    const [resolvingOpen, setResolvingOpen] = useState(false);

    const UPLOAD_STEP = 2;
    const CONTEST_STEP = 3;

    useEffect(() => {
        setLoading(true);
        setUserDocument(null);
        setTalentDocumentSpec(null);

        if (talent) {
            Promise.all([
                loadUserTalentDocuments(null, talent),
                loadTalentDocument(talent),
            ]).then(([usertalentDocuments, talentDoc]) => {
                setLoading(false);
                setTalentDocumentSpec(talentDoc || null);

                const docs = Array.isArray(usertalentDocuments) ? usertalentDocuments : [];
                if (docs.length > 0) {
                    var ud = docs[0];
                    setUserDocument(ud);
                    setUploadEnabled(false);
                } else {
                    setUploadEnabled(true);
                }
            }).catch(() => {
                setLoading(false);
                setTalentDocumentSpec(null);
                setUploadEnabled(true);
            });
        } else {
            setLoading(false);
        }
    }, [talent]);

    useEffect(() => {
        if (step !== 1 && requirementsVideoRef.current) {
            requirementsVideoRef.current.pause();
        }
    }, [step]);

    /**
     * On open: if the user already has a contest-matching talent + video, jump to Participate.
     * If they have the talent but no video, start at requirements. Otherwise stay on talent select.
     */
    useEffect(() => {
        const el = document.getElementById("addUserTalentModal");
        if (!el) return undefined;

        const resetWizard = () => {
            setStep(0);
            setTalent(null);
            setUserDocument(null);
            setTalentDocumentSpec(null);
            setUploadEnabled(false);
            setError(null);
            setResolvingOpen(false);
            setLoading(false);
        };

        const resolveOpeningStep = async () => {
            const requestId = ++resolveGen.current;
            setResolvingOpen(true);
            setError(null);
            try {
                const [userTalents, coursesList] = await Promise.all([
                    loadUserTalents({ size: 500 }),
                    loadCourses({ size: 500 }),
                ]);
                if (requestId !== resolveGen.current) return;

                const list = Array.isArray(userTalents) ? userTalents : [];
                const newest = pickNewestNewContest(coursesList || []);
                const contestIds = new Set(getCourseTalentIds(newest));
                const userSet = collectUserTalentAncestorIds(list);
                const canJoin = newest
                    ? userCanParticipateInContest(userSet, newest)
                    : false;

                const sorted = [...list].sort((a, b) => {
                    const aMatch = talentMatchesContest(a, contestIds) ? 1 : 0;
                    const bMatch = talentMatchesContest(b, contestIds) ? 1 : 0;
                    return bMatch - aMatch;
                });

                const candidates =
                    canJoin && contestIds.size
                        ? sorted.filter((ut) => talentMatchesContest(ut, contestIds))
                        : sorted;

                for (const ut of candidates) {
                    const talentId = ut?.talent?.id;
                    if (talentId == null) continue;
                    const docsRaw = await loadUserTalentDocuments(null, talentId);
                    if (requestId !== resolveGen.current) return;
                    const docs = Array.isArray(docsRaw) ? docsRaw : [];
                    if (docs.length > 0) {
                        setTalent(talentId);
                        setUserDocument(docs[0]);
                        setUploadEnabled(false);
                        setStep(CONTEST_STEP);
                        setResolvingOpen(false);
                        return;
                    }
                }

                if (candidates.length && candidates[0]?.talent?.id != null) {
                    setTalent(candidates[0].talent.id);
                    setStep(1);
                    setResolvingOpen(false);
                    return;
                }

                setTalent(null);
                setStep(0);
            } catch (_) {
                if (requestId !== resolveGen.current) return;
                setTalent(null);
                setStep(0);
            } finally {
                if (requestId === resolveGen.current) {
                    setResolvingOpen(false);
                }
            }
        };

        const onShow = () => {
            resolveOpeningStep();
        };

        const onHidden = () => {
            resolveGen.current += 1;
            resetWizard();
            if (typeof onVideosChanged === "function") {
                onVideosChanged();
            }
        };

        el.addEventListener("show.bs.modal", onShow);
        el.addEventListener("hidden.bs.modal", onHidden);
        return () => {
            el.removeEventListener("show.bs.modal", onShow);
            el.removeEventListener("hidden.bs.modal", onHidden);
        };
    }, [onVideosChanged]);

    const [{ translations }] = useStateValue();
    const translate = (str) => {
        if (!translations) return str;
        var translate = translations.find(x => x.key == str);

        if (translate) return getDefaultValue(translate);
        else return "#" + str;
    };

    const requirementsTutorialSrc = useMemo(() => {
        const raw = talentDocumentSpec?.names && getDefaultTutorialVideo(talentDocumentSpec.names);
        if (raw) return resolveMediaUrl(raw);
        return REQUIREMENTS_TUTORIAL_VIDEO_SRC;
    }, [talentDocumentSpec]);

    const _next = () => {
        setError("");
        if (_canGoNext()) {
            setStep(step + 1);
        }
    };

    const _finish = () => {
        btnCloseModal.current.click()
    }

    const handleContestParticipationChange = () => {
        if (typeof onContestParticipationChange === "function") {
            onContestParticipationChange();
        }
        if (!talent) return;

        setLoading(true);
        loadUserTalentDocuments(null, talent)
            .then((documents) => {
                const latestDocument = Array.isArray(documents) ? documents[0] : null;
                setUserDocument(latestDocument || null);
                setUploadEnabled(!latestDocument);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const _canGoNext = () => {
        setError("")
        if (step === 0) {
            if (!talent) {
                setError("Please select a talent")
                return false;
            }
        }
        else if (step === 1 && loading) {
            return false;
        }
        else if (step === UPLOAD_STEP) {
            if (!userDocument) {
                setError("Please add a document");
                return false;
            }
        }
        return true;
    };


    const _back = () => {
        if (step > 0) {
            if (step === UPLOAD_STEP && userDocument) {
                setUploadEnabled(false);
            }
            setStep(step - 1);
        }
    };


    // Nested react-modal (complete profile) renders outside this node; without data-focus="false",
    // Bootstrap's focus trap pulls focus back into this modal and breaks nested form controls.
    return (
        <div
            className="modal fade lan-rtl add-user-talent-modal"
            id="addUserTalentModal"
            tabIndex="-1"
            role="dialog"
            aria-hidden="true"
            data-focus="false"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable w-700">
                <div className="modal-content bg-upload-material">
                    <div className="modal-header modal-header--polished no-border pb-0">
                        <h2 className="modal-title-polished lan-text-right"></h2>
                        <button type="button" ref={btnCloseModal} className="close modal-close-ghost" data-dismiss="modal" aria-label="Close">&times;</button>
                    </div>
                    <div className="modal-body padding-top-0">
                        {resolvingOpen ? (
                            <LoadingOverlay loading />
                        ) : (
                        <div className="steps">
                            <div className={`step step-select-talent ${step === 0 ? 'step-active' : ""}`}>
                                <h2>{translate("Please choose your talent")}</h2>
                                <SelectTalent
                                    onselect={(name, value) => {
                                        setError("");
                                        setTalent(value);
                                        setStep(1);
                                    }}
                                />
                            </div>
                            <div className={`step ${step === 1 ? 'step-active' : ''}`}>
                                <div className="row lan-rtl align-items-start">
                                    <div className="col-md-6 lan-text-right">
                                        <div className="font-color-silver p-2 text-4">
                                            {talentDocumentSpec && talentDocumentSpec.names
                                                ? getDefaultDescription(talentDocumentSpec.names)
                                                : translate('No detailed requirements are available for this talent. You can continue to upload your document.')}
                                        </div>
                                    </div>
                                    <div className="col-md-6 p-0"
                                        style={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '280px',
                                        }}
                                    >
                                        <div className="add-user-talent-requirements-video my-1 text-right lan-text-left"
                                        style={{
                                           border: '1px solid #504b4b',
                                           width: '80%',
                                        }}
                                        >
                                            <video
                                                key={requirementsTutorialSrc}
                                                ref={requirementsVideoRef}
                                                controls
                                                playsInline
                                                className="w-100 border-radius-0"
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    objectFit: 'contain',
                                                }}
                                                preload="metadata"
                                            >
                                                <source src={requirementsTutorialSrc} type="video/mp4" />
                                            </video>
                                        </div>
                                    </div>
                                </div>
                                <LoadingOverlay key={String(loading) + 'req'} loading={loading && !talentDocumentSpec && !!talent} />
                            </div>
                            <div className={`step  ${step === UPLOAD_STEP ? 'step-active' : ''}`}>
                                {(!upload_enabled && userDocument) && (
                                    <UserDocumentPreview
                                        handleEdit={() => setUploadEnabled(true)}
                                        userDocument={userDocument}
                                        setUserDocument={(val) => {
                                            setUserDocument(val);
                                            if (!val) setUploadEnabled(true);
                                        }}
                                    />
                                )}
                                {upload_enabled && (
                                    <UploadDocumentForm
                                        talentId={talent}
                                        userDocumentId={userDocument && userDocument.id}
                                        onCancel={userDocument ? () => setUploadEnabled(false) : undefined}
                                        onSuccess={(doc) => {
                                            setError("");
                                            setUserDocument(doc);
                                            if (doc) setUploadEnabled(false);
                                            if (typeof onVideosChanged === "function") {
                                                onVideosChanged();
                                            }
                                        }}
                                    />
                                )}
                                <LoadingOverlay key={loading + "ld"} loading={loading} />
                            </div>
                            <div className={`step ${step === CONTEST_STEP ? 'step-active' : ''}`}>
                                <ContestParticipation
                                    key={talent}
                                    talentId={talent}
                                    contestViewHideImage
                                    hideUnrollWhenEnrolled
                                    onParticipationChange={handleContestParticipationChange}
                                />
                            </div>
                        </div>
                        )}
                    </div>
                    <div className="footer-flex-center">
                        {error &&
                            <>
                                <div className="alert alert-danger text-2">
                                    {translate(error)}
                                </div>
                            </>
                        }
                        <StepIndicator index={step} steps={4} />
                        <hr className="full-width my-3" />
                        <div className="modal-step-actions">
                            <div>
                                {step > 0 && !resolvingOpen && (
                                    <button className="btn btn-previous" onClick={_back}>{translate('Back')}</button>

                                )}
                            </div>
                            <div>

                                {step < CONTEST_STEP && !resolvingOpen && (
                                    <button
                                        type="button"
                                        className="btn btn-outlined"
                                        onClick={_next}
                                        disabled={step === 1 && loading}
                                    >
                                        {translate('Next')}
                                    </button>
                                )}
                                {step === CONTEST_STEP && !resolvingOpen && (
                                    <button className="btn btn-outlined" onClick={_finish}>{translate('Finish')}</button>
                                )}
                            </div>

                        </div>

                    </div>

                </div>
            </div>
        </div>
    )
}

export default AddUserTalentModal
